// Předpokládané naskladnění vyprodaného zboží: „Naskladnění očekáváme do 8. 10."
// (handover reporting-saas 2026-09-24, rozhodnutí vlastníka: datum + rezerva,
// detail i karty, CZ i SK, noční čerstvost stačí).
//
// Zdroj = `/specs` → `restock.expected_at` (search API čte noční export
// objednávek u dodavatelů, search/api/restock.mjs). Ukazuje se datum příjmu
// posunuté o REZERVU `catalog.restockBufferDays` pracovních dní z manifestu
// shopu; pracovní dny = svátky ČR z dispatchEta.js (sklad je společný i pro SK).
//
// Kdy se nekreslí nic:
//   • přepínač `restockDate` (features.js) je pro projekt vypnutý;
//   • stránka NEukazuje vyprodaný stav — soubor nese i kódy, které jsou právě
//     skladem, rozhoduje text dostupnosti na kartě / panel vyprodaného na detailu;
//   • variantní produkt (kód karty je master, termín patří variantě);
//   • `expected_at` je před dneškem (soubor může být až 24 h starý) nebo chybí.
//
// Detail: věta „Nevíme, kdy bude znovu skladem" v panelu vyprodaného (#227) se
// nahradí termínem. Karty: řádek pod nativní dostupností. Nativní výpis
// kategorie vyprodané zboží na ostré nezobrazuje (Ventilátory 24. 9.: 23 z 27
// vyprodaných, 0 karet) — vyprodanou kartu reálně kreslí hlavně výsledky
// hledání a doporučení z indexu (`data-bs-code`) a Související na detailu.
import SHOP from '../../config/shop.js';
import { isWorkday } from './dispatchEta.js';
import { featureOn } from './features.js';
import { icon } from './icon.js';
import { specFor } from './specsCache.js';
import L from '../locale/cs/restockDate.js';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → lokální půlnoc, jinak `null` (i neexistující den 2026-02-30). */
export function parseDay(value) {
  const m = DATE_RE.exec(typeof value === 'string' ? value : '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) return null;
  return d;
}

/**
 * Den, který vidí zákazník: `expectedAt` + `buffer` pracovních dní (víkendy
 * a svátky ČR se přeskakují). `null` = nekreslit (neplatné datum nebo příjem
 * před dneškem).
 */
export function restockDay(expectedAt, now = new Date(), buffer = SHOP.catalog.restockBufferDays) {
  const day = parseDay(expectedAt);
  if (!day) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (day < today) return null;
  let left = Math.max(0, Math.floor(Number(buffer) || 0));
  while (left > 0) {
    day.setDate(day.getDate() + 1);
    if (isWorkday(day)) left--;
  }
  return day;
}

/** „8. 10.", v jiném roce než dnes „8. 1. 2027". */
export function formatDay(date, now = new Date()) {
  const base = date.getDate() + '. ' + (date.getMonth() + 1) + '.';
  return date.getFullYear() === now.getFullYear() ? base : base + ' ' + date.getFullYear();
}

/** Zobrazený termín z položky `/specs`, nebo `null`. */
export function restockLabel(item, now = new Date()) {
  const restock = item && item.restock;
  const day = restock ? restockDay(restock.expected_at, now) : null;
  return day ? formatDay(day, now) : null;
}

/** Karta ukazuje vyprodaný stav (nativní text dostupnosti, i karta z indexu). */
export function isSoldOutCard(card) {
  const avail = card && card.querySelector('.availability');
  return !!avail && L.soldOutRe.test(avail.textContent || '');
}

/** Stojí karta o termín? (přepínač, vyprodaná, ne varianty, ještě bez řádku) */
export function wantsCardRestock(card) {
  if (!featureOn('restockDate') || !card) return false;
  if (card.dataset.bsVariants || card.querySelector('.bs-card__restock')) return false;
  return isSoldOutCard(card);
}

/** Řádek „Naskladnění očekáváme do …" pod dostupnost karty. Idempotentní. */
export function mountCardRestock(card, item, now) {
  if (!wantsCardRestock(card)) return;
  const label = restockLabel(item, now);
  if (!label) return;
  const line = document.createElement('div');
  line.className = 'bs-card__restock';
  line.appendChild(icon('calendar'));
  const txt = document.createElement('span');
  txt.textContent = L.expected(label);
  line.appendChild(txt);
  card.querySelector('.availability').insertAdjacentElement('afterend', line);
}

/**
 * Panel vyprodaného produktu na detailu: text pod titulkem („Nevíme, kdy bude
 * znovu skladem…") se po odpovědi `/specs` nahradí termínem. Bez termínu zůstává.
 *
 * @param {HTMLElement|null} textEl `.bs-soldout__text`
 * @param {{code?: string, hasVariants?: boolean}|null} product dataLayer `shoptet.product`
 */
export function mountDetailRestock(textEl, product) {
  if (!featureOn('restockDate') || !textEl || !product || !product.code || product.hasVariants === true) return;
  specFor(product.code).then(function (item) {
    const label = restockLabel(item);
    if (!label || textEl.querySelector('.bs-soldout__restock')) return;
    textEl.textContent = '';
    const strong = document.createElement('b');
    strong.className = 'bs-soldout__restock';
    strong.textContent = L.expected(label);
    textEl.appendChild(strong);
    textEl.appendChild(document.createTextNode(' ' + L.detailRest));
  });
}
